/**
 * DarkInstrumentCover — K5 additive, OPT-IN dark "instrument-panel" title page.
 *
 * The redesign reference (`Redhead_BTM_Feasibility_Redesign_1.pdf` p1) gives
 * the document gravity with a DARK cover: faint concentric reactor-datum rings,
 * mono engineering datum strips top + bottom, a serif display title, a verdict
 * chip, and a bottom KPI rail with serif numerals. This re-skins that rhythm
 * with OUR theme tokens — the dark surface + on-dark text ramp + accent are all
 * DERIVED from the active theme via `resolveCoverDarkSurface(theme)` (NOT the
 * reference's near-black/amber skin). A journal/institutional/monograph preset
 * keeps its OWN ink-derived surface and primary accent.
 *
 * ADDITIVE: this cover is never a preset default and resolves only when a
 * user/template explicitly selects `'dark-instrument'`. It does NOT carry the
 * exec summary (it is a decision-first title page), so the standalone body
 * Executive Summary section still renders — no de-dup change.
 *
 * NULL-SAFE (NO DATA LOSS): the title comes from the ONE cover-title resolver,
 * the verdict from the canonical `getDisplayVerdict`, the KPI rail from the
 * persisted TRACK-B snapshots via `resolveDataSnapshotFigures` (legacy reports
 * render em-dash figures, never NaN / throw). Coordinates come from the site
 * centroid; absent geometry collapses the coordinate datum cells gracefully.
 *
 * GATED chrome: the corner crop marks render only when the theme opts in via
 * `coverBrandAffordances.cropMarks`. The datum STRIPS are part of this cover's
 * defining motif (always on for this layout) but additionally render their
 * coordinate labels only when geometry is present.
 */
import React from 'react';
import {
  resolveCoverTitle,
  resolveCoverKicker,
  resolveSiteWordmark,
  resolveDataSnapshotFigures,
} from '../../../../lib/reportRendering/journalCoverData';
import { getDisplayVerdict } from '../../../../lib/reportVerdict';
import { roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes } from '../../../../lib/reportSectionHeading';
import {
  resolveCoverDarkSurface,
  coverBrandAffordancesEnabled,
  COVER_MIN_HEIGHT_PX,
  COVER_METADATA_GRID_CLASS,
  COVER_DISCLAIMER_FOOTER_CLASS,
} from '../../../../lib/reportCoverTokens';
import { CropMarks, CoverDatumStrip, ConcentricRings } from './coverBrandAffordances';
import type { CoverProps } from './types';
import { ReportPlatformCredit } from '../ReportPlatformCredit';

/** Format a signed lat/lng as a mono datum string (e.g. "30.3533°N 93.2487°W").
 *  Returns null when no centroid is present. */
function resolveCoordinateDatum(lat?: number, lng?: number): string | null {
  if (typeof lat !== 'number' || !Number.isFinite(lat)) return null;
  if (typeof lng !== 'number' || !Number.isFinite(lng)) return null;
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lng >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(4)}°${ns}  ${Math.abs(lng).toFixed(4)}°${ew}`;
}

export const DarkInstrumentCover: React.FC<CoverProps> = ({
  report, theme,
  branding,
  disclaimer,
}) => {
  const dark = resolveCoverDarkSurface(theme);
  const title = resolveCoverTitle(report);
  const kicker = resolveCoverKicker(theme.companyName, report);
  const wordmark = resolveSiteWordmark(report, branding);

  const verdict = getDisplayVerdict(report);
  const verdictWord = verdict.label && verdict.label !== '—' ? verdict.label : 'Assessment';

  // KPI rail — the persisted TRACK-B figures (firm / reserve / LCOE / COD),
  // null-safe (em-dash when a snapshot is absent).
  const figures = resolveDataSnapshotFigures(report);

  const centroid = report.siteCentroid;
  const coordinate = resolveCoordinateDatum(centroid?.lat, centroid?.lng);
  const locationParts = [report.address?.trim()].filter(Boolean) as string[];
  const location = locationParts.join(' · ');

  const cropMarksOn = coverBrandAffordancesEnabled(theme, 'cropMarks');
  const displayFont = theme.displayFont || theme.headingFont;
  const monoLabel = theme.bodyFont;
  const titleSize = `clamp(34px, 6vw, calc(${reportTypographySizes(theme).h2} * 3))`;

  return (
    <div
      className="relative flex flex-col overflow-hidden"
      data-testid="cover-dark-instrument"
      data-cover-variant="dark-instrument"
      data-verdict-tier={verdict.tier}
      style={{
        backgroundColor: dark.surface,
        color: dark.text,
        minHeight: COVER_MIN_HEIGHT_PX,
        // Pad the dark surface so it reads as a full-bleed instrument panel
        // within the cover's content box.
        padding: '28px 30px',
      }}
    >
      {/* Concentric reactor-datum rings — the defining motif, behind everything. */}
      <ConcentricRings color={roleColorAlpha(theme, 'accent', 0.16)} />

      {/* GATED corner crop marks (off unless theme opts in). */}
      {cropMarksOn && <CropMarks color={dark.hair} testId="cover-dark-instrument-crop-marks" />}

      {/* TOP datum: wordmark + engine label, then a dotted coordinate strip. */}
      <div className="relative z-10">
        <div
          className="flex items-baseline justify-between text-[10px] uppercase tracking-[0.2em]"
          style={{ fontFamily: monoLabel, color: dark.text }}
        >
          <span className="font-bold" data-testid="cover-dark-instrument-wordmark">{wordmark}</span>
          <span style={{ color: dark.muted }} data-testid="cover-dark-instrument-engine">{kicker}</span>
        </div>
        <div className="mt-3">
          <CoverDatumStrip
            color={dark.hair}
            labelColor={dark.muted}
            left={coordinate ?? undefined}
            right={location || undefined}
            fontFamily={monoLabel}
            testId="cover-dark-instrument-datum-top"
          />
        </div>
      </div>

      {/* CENTRE: serif kicker + display title + verdict chip. */}
      <div className="relative z-10 mt-auto pt-10">
        <div
          className="text-[11px] font-bold uppercase tracking-[0.24em]"
          style={{ fontFamily: monoLabel, color: dark.accent }}
          data-testid="cover-dark-instrument-kicker"
        >
          {kicker}
        </div>
        <h1
          id="cover-title"
          className="mt-3 leading-[0.98] max-w-[16ch]"
          style={{
            fontFamily: displayFont,
            color: dark.text,
            fontSize: titleSize,
            fontWeight: 500,
            letterSpacing: '-0.01em',
          }}
          data-testid="cover-dark-instrument-title"
        >
          {title}
        </h1>
        <div className="mt-6">
          <span
            className="inline-block text-[10px] font-bold uppercase tracking-[0.16em] px-2.5 py-1"
            style={{
              color: dark.accent,
              border: `1px solid ${roleColorAlpha(theme, 'accent', 0.55)}`,
            }}
            data-testid="cover-dark-instrument-verdict"
          >
            {`Verdict – ${verdictWord}`}
          </span>
        </div>
      </div>

      {/* BOTTOM datum strip + KPI rail with serif numerals. */}
      <div className="relative z-10 mt-auto pt-10">
        <CoverDatumStrip color={dark.hair} testId="cover-dark-instrument-datum-bottom" />
        <div
          className={`${COVER_METADATA_GRID_CLASS} mt-4`}
          data-testid="cover-dark-instrument-kpi-rail"
        >
          {figures.map((fig, idx) => (
            <div
              key={fig.id}
              className="pr-4"
              style={{
                borderRight:
                  idx < figures.length - 1 ? `1px solid ${dark.hair}` : undefined,
              }}
              data-testid={`cover-dark-instrument-kpi-${fig.id}`}
            >
              <div
                className="text-[8px] font-bold uppercase tracking-[0.18em]"
                style={{ fontFamily: monoLabel, color: dark.muted }}
              >
                {fig.label}
              </div>
              <div
                className="mt-1.5 leading-none tabular-nums"
                style={{ fontFamily: displayFont, color: dark.text, fontSize: 30 }}
              >
                {fig.value ?? '–'}
                {fig.value != null && fig.unit && (
                  <span
                    className="ml-1 text-[12px]"
                    style={{
                      fontFamily: monoLabel,
                      // The grid-gap / LCOE figure picks up the accent on the
                      // dark surface (the reference's amber "250 MW" treatment),
                      // re-skinned to OUR accent.
                      color: fig.id === 'lcoe' ? dark.accent : dark.muted,
                    }}
                  >
                    {fig.unit}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONFIDENTIAL DISCLAIMER — bottom-right footnote, on-dark. */}
      <div
        className={`${COVER_DISCLAIMER_FOOTER_CLASS} z-10`}
        style={{ color: dark.faint }}
        data-testid="cover-dark-instrument-disclaimer"
      >
        {disclaimer}
      </div>

      {/* GATEWAY POWER platform credit — top-RIGHT, inside the panel's 28px top
          padding, on the 30px content column (absolute: adds no height). On-dark:
          the brand's dark artwork (white + teal) in the panel's faint ink. The
          foot is taken by the bottom-right disclaimer, which wraps into the
          metrics row on narrow screens; the top padding is free at any width. */}
      <ReportPlatformCredit
        tone="on-dark"
        color={dark.faint}
        className="absolute top-2 right-[30px] z-10"
        testId="cover-dark-instrument-platform-credit"
      />
    </div>
  );
};
